import { useEffect, useState } from 'react';
import { useAuth } from '@tsuyosa/auth';
import { getMyOrders } from '@/lib/api';
import type { MyOrder } from '@/lib/api';
import type { ChekiOrderStatus } from '@tsuyosa/types';

const STATUS_LABELS: Record<ChekiOrderStatus, string> = {
  pending:   'Pending',
  paid:      'Lunas',
  done:      'Selesai',
  cancelled: 'Dibatalkan',
};

const STATUS_COLORS: Record<ChekiOrderStatus, string> = {
  pending:   'text-amber-400 bg-amber-400/10',
  paid:      'text-blue-400 bg-blue-400/10',
  done:      'text-green-400 bg-green-400/10',
  cancelled: 'text-red-400 bg-red-400/10',
};

function formatRupiah(n: number) {
  return `Rp${n.toLocaleString('id-ID')}`;
}

export function OrderHistoryPage() {
  const { accessToken } = useAuth();

  const [orders, setOrders]   = useState<MyOrder[]>([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]     = useState('');
  const [statusFilter, setStatusFilter] = useState<ChekiOrderStatus | undefined>();

  const limit = 10;

  useEffect(() => {
    if (!accessToken) return;
    setIsLoading(true);
    setError('');
    getMyOrders(accessToken, { page, status: statusFilter })
      .then(res => { setOrders(res.orders); setTotal(res.pagination.total); })
      .catch(() => setError('Gagal memuat riwayat order.'))
      .finally(() => setIsLoading(false));
  }, [accessToken, page, statusFilter]);

  const totalPages = Math.ceil(total / limit);

  const statusOptions: Array<{ value: ChekiOrderStatus | undefined; label: string }> = [
    { value: undefined, label: 'Semua' },
    { value: 'pending',   label: 'Pending' },
    { value: 'paid',      label: 'Lunas' },
    { value: 'done',      label: 'Selesai' },
    { value: 'cancelled', label: 'Dibatalkan' },
  ];

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <h1 className="mb-2 text-2xl font-bold text-white">Riwayat Order</h1>
      <p className="mb-6 text-sm text-white/50">Semua pre-order cheki yang terhubung ke akunmu.</p>

      {/* Filter */}
      <div className="mb-5 flex flex-wrap gap-2">
        {statusOptions.map(opt => (
          <button
            key={opt.label}
            onClick={() => { setPage(1); setStatusFilter(opt.value); }}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
              statusFilter === opt.value
                ? 'border-[#f472b6]/50 bg-[#f472b6]/15 text-[#f472b6]'
                : 'border-white/10 bg-white/5 text-white/50 hover:border-white/20'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {error && (
        <p className="mb-4 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400">{error}</p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <span className="h-8 w-8 animate-spin rounded-full border-2 border-[#f472b6] border-t-transparent" />
        </div>
      ) : orders.length === 0 ? (
        <div className="rounded-xl border border-white/5 bg-[#130d1a] p-8 text-center">
          <p className="text-white/40">Belum ada order cheki.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {orders.map(order => (
            <div key={order.id} className="rounded-xl border border-white/5 bg-[#130d1a] p-4">
              <div className="mb-2 flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-white">{order.schedule_title}</p>
                  <p className="text-sm text-white/50">{order.schedule_date}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[order.status]}`}>
                  {STATUS_LABELS[order.status]}
                </span>
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/50">
                <span>{order.type === '2shot_solo' ? `2-Shot · ${order.member_nickname ?? '-'}` : 'All Member'}</span>
                <span>× {order.quantity}</span>
                <span className="font-medium text-white/80">{formatRupiah(order.total_price)}</span>
              </div>

              <p className="mt-2 font-mono text-xs text-white/20">{order.id}</p>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/60 hover:bg-white/5 disabled:opacity-40 transition"
          >
            ← Sebelumnya
          </button>
          <span className="text-sm text-white/40">{page} / {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/60 hover:bg-white/5 disabled:opacity-40 transition"
          >
            Selanjutnya →
          </button>
        </div>
      )}
    </div>
  );
}
